"use client";

import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import { Device, DeviceInfo } from "@capacitor/device";
import { useRouter } from "next/navigation";
import useCapacitor from "@/hooks/useCapacitor";
import { useAuth } from "../auth/Auth";
import { commonApiPost } from "@/services/api/common-api";
import { getStableDeviceId } from "./stable-device-id";
import { ApiIdentity } from "@/generated/models/ApiIdentity";
type NotificationsContextType = {
  removeWaveDeliveredNotifications: (waveId: string) => Promise<void>;
  removeAllDeliveredNotifications: () => Promise<void>;
};
const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);
const redirectConfig = {
  path: ({
    path
  }: {
    path: string;
  }) => `/${path}`,
  profile: ({
    handle
  }: {
    handle: string;
  }) => `/${handle}`,
  "the-memes": ({
    id
  }: {
    id: string;
  }) => `/the-memes/${id}`,
  "6529-gradient": ({
    id
  }: {
    id: string;
  }) => `/6529-gradient/${id}`,
  "meme-lab": ({
    id
  }: {
    id: string;
  }) => `/meme-lab/${id}`,
  waves: ({
    wave_id,
    drop_id
  }: {
    wave_id: string;
    drop_id: string;
  }) => {
    const base = `/waves?wave=${wave_id}`;
    return drop_id ? `${base}&serialNo=${drop_id}` : base;
  }
};
export const NotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    isCapacitor,
    isIos
  } = useCapacitor();
  const {
    connectedProfile
  } = useAuth();
  const router = useRouter();
  const initializationRef = useRef<string | null>(null);
  useEffect(() => {
    const profileId = connectedProfile?.id ?? null;
    if (isCapacitor && initializationRef.current !== profileId) {
      initializationRef.current = profileId;
      initializeNotifications(connectedProfile ?? undefined);
    }
  }, [connectedProfile, isCapacitor]);
  const initializeNotifications = async (profile?: ApiIdentity) => {
    try {
      if (isCapacitor) {
        console.log("Initializing push notifications");
        await initializePushNotifications(profile);
      }
    } catch (error) {
      console.error("Error initializing notifications", error);
    }
  };
  const initializePushNotifications = async (profile_0?: ApiIdentity) => {
    try {
      await PushNotifications.removeAllListeners();
      const stableDeviceId = await getStableDeviceId();
      const deviceInfo = await Device.getInfo();
      await PushNotifications.addListener("registration", async token => {
        registerPushNotification(stableDeviceId, deviceInfo, token.value, profile_0);
      });
      await PushNotifications.addListener("registrationError", error_1 => {
        console.error("Push registration error: ", error_1);
      });
      await PushNotifications.addListener("pushNotificationReceived", notification => {
        console.log("Push notification received: ", notification);
      });
      await PushNotifications.addListener("pushNotificationActionPerformed", async action => {
        await handlePushNotificationAction(router, action.notification, profile_0);
      });
      const permStatus = await PushNotifications.requestPermissions();
      console.log("Push permission status", permStatus);
      if (permStatus.receive === "granted") {
        if (isIos) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        try {
          await PushNotifications.register();
        } catch (registerError: any) {
          const errorMessage = registerError?.message || String(registerError);
          if (errorMessage.includes("capacitorDidRegisterForRemoteNotifications") || errorMessage.includes("didRegisterForRemoteNotifications")) {
            console.warn("iOS push notification registration callback issue. This may occur if the native delegate is not properly configured.", registerError);
          } else {
            throw registerError;
          }
        }
      } else {
        console.warn("Push notifications permission not granted");
      }
    } catch (error_0) {
      console.error("Error in initializePushNotifications", error_0);
    }
  };
  const handlePushNotificationAction = async (router_0: ReturnType<typeof useRouter>, notification_0: PushNotificationSchema, profile_1?: ApiIdentity) => {
    console.log("Push notification action performed", notification_0);
    const notificationData = notification_0.data;
    const notificationProfileId = notificationData.profile_id;
    if (profile_1 && notificationProfileId && notificationProfileId !== profile_1.id) {
      console.log("Notification profile id does not match connected profile");
      return;
    }
    void removeDeliveredNotifications([notification_0]);
    const redirectUrl = resolveRedirectUrl(notificationData);
    if (redirectUrl) {
      console.log("Redirecting to", redirectUrl);
      router_0.push(redirectUrl);
    } else {
      console.log("No redirect url found in notification data", notificationData);
    }
  };
  const removeDeliveredNotifications = async (notifications: PushNotificationSchema[]) => {
    if (isIos) {
      try {
        await PushNotifications.removeDeliveredNotifications({
          notifications
        });
      } catch (error_2) {
        console.error("Error removing delivered notifications", error_2);
      }
    }
  };
  const removeWaveDeliveredNotifications = async (waveId: string) => {
    if (isIos) {
      const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
      const waveNotifications = deliveredNotifications.notifications.filter(notification_1 => notification_1.data.wave_id === waveId);
      await removeDeliveredNotifications(waveNotifications);
    }
  };
  const removeAllDeliveredNotifications = async () => {
    if (isIos) {
      try {
        await PushNotifications.removeAllDeliveredNotifications();
      } catch (error_3) {
        console.error("Error removing all delivered notifications", error_3);
      }
    }
  };
  const value = useMemo(() => ({
    removeWaveDeliveredNotifications,
    removeAllDeliveredNotifications
  }), []);
  return <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>;
};
const registerPushNotification = async (deviceId: string, deviceInfo: DeviceInfo, token: string, profile?: ApiIdentity) => {
  try {
    const response = await commonApiPost({
      endpoint: `push-notifications/register`,
      body: {
        device_id: deviceId,
        token,
        platform: deviceInfo.platform,
        profile_id: profile?.id
      }
    });
    console.log("Push registration success", response);
  } catch (error) {
    console.error("Push registration error", error);
  }
};
const resolveRedirectUrl = (notificationData: any) => {
  const {
    redirect,
    ...params
  } = notificationData;
  if (!redirect) {
    console.log("No redirect type found in notification data", notificationData);
    return null;
  }
  const resolveFn = redirectConfig[redirect as keyof typeof redirectConfig];
  if (!resolveFn) {
    console.error("Unknown redirect type", redirect);
    return null;
  }
  try {
    return resolveFn(params);
  } catch (error) {
    console.error("Error resolving redirect URL", error);
    return null;
  }
};
export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotificationsContext must be used within a NotificationsProvider");
  }
  return context;
};